import io, { Socket } from 'socket.io-client';
import { visibility } from '@betgames/bg-tools';
import { TwainGameId } from 'betgames-contract-js';

const REFRESH_CONNECTION_INTERVAL = 900000;
const RECONNECTION_CHECK_INTERVAL = 5000;

export default class WebSocketsRepository {
    private instance: Socket;

    private readonly subscriptions: Set<string> = new Set();

    private token: string;

    private getClient = (): Socket => {
        if (!this.instance) {
            throw Error('WS instance not initialized.');
        }

        return this.instance;
    };

    public init = (url: string, jwt: string, presetsIds: Record<TwainGameId, number>): void => {
        this.instance = io(url, {
            reconnectionAttempts: 5, // TODO change for go-live?
            transports: ['websocket'],
        });
        this.token = jwt;
        let disconnected = false;
        let isHiddenStatus = false;
        let timeoutId = 0;
        let reconnectId = 0;

        const emitAuthentication = (): void => {
            this.getClient().emit('authentication', { jwt: this.token, presetsIds });

            setInterval(() => {
                this.getClient().emit('refresh_connection', this.token);
            }, REFRESH_CONNECTION_INTERVAL);
        };

        visibility.setEvent((isHidden: boolean) => {
            window.clearTimeout(timeoutId);
            window.clearTimeout(reconnectId);
            isHiddenStatus = isHidden;

            // setTimeout is added here because function should to be executed
            // after socket disconnection callback (jump to end of callstack)
            timeoutId = window.setTimeout(() => {
                if (!isHidden && disconnected) {
                    window.location.reload();
                }
            }, 0);
        });

        window.addEventListener(
            'beforeunload',
            () => {
                this.getClient().disconnect();
            },
            false,
        );

        this.instance
            .on('connect', () => {
                window.clearTimeout(reconnectId);
                this.getClient().off('connect');
                emitAuthentication();

                this.subscriptions.forEach((channel) => {
                    this.getClient().emit('subscribe', channel);
                });
            })
            .on('reconnect', () => {
                emitAuthentication();

                this.subscriptions.forEach((channel) => {
                    this.getClient().emit('subscribe', channel);
                });
            })
            .on('disconnect', (reason: Socket.DisconnectReason) => {
                if (reason === 'io client disconnect') {
                    this.getClient().connect();
                }

                disconnected = true;

                reconnectId = window.setInterval(() => {
                    if (isHiddenStatus) {
                        window.clearTimeout(reconnectId);
                    } else if (this.getClient().connected) {
                        window.clearTimeout(reconnectId);
                        emitAuthentication();

                        this.subscriptions.forEach((channel) => {
                            this.getClient().emit('subscribe', channel);
                        });
                    }
                }, RECONNECTION_CHECK_INTERVAL);
            });
    };

    public on = <T>(event: string, listener: ICallback<T>) => {
        const listenerWrapper = (data: unknown): void => {
            let parsedData = data as T;

            if (typeof parsedData === 'string') {
                try {
                    parsedData = JSON.parse(data as string);
                } catch {}
            }

            listener(parsedData);
        };

        this.getClient().on(event, listenerWrapper);

        return () => {
            this.getClient().removeListener(event, listenerWrapper);
        };
    };

    public emit = (event: string, data: unknown) => {
        this.getClient().emit(event, data);
    };

    public subscribe = (channel: string) => {
        this.subscriptions.add(channel);
        this.getClient().emit('subscribe', channel);

        return () => {
            this.subscriptions.delete(channel);
            this.getClient().emit('unsubscribe', channel);
        };
    };

    public setToken = (token: string, forceUpdateToken?: boolean) => {
        this.token = token;

        if (forceUpdateToken) {
            this.getClient().emit('refresh_connection', token);
        }
    };
}
